import React, { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageTitle from "../../Common/PageTitle";
import Spinner from "../../Common/Spinner";
import ConfigService from "../../../services/ConfigService";
import CommonUtiles from "../../../services/CommonUtiles";
import {
  NOT_DATA_FOUND,
  RANKED_COMPANIES_BY_LOGIN_COUNT,
  RANKED_DEPARTMENTS_BY_LOGIN_COUNT,
} from "../../../services/Constant";

const StatisticsData = () => {
  const [loading, setLoading] = useState(false);
  const [userloading, setUserLoading] = useState(false);
  const [statisticsData, setStatisticsData] = useState({});
  const history = useHistory();
  const [sort, setSort] = useState({
    companies: { column: "rank", direction: "asc" },
    departments: { column: "rank", direction: "asc" },
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minStartDate, setMinStartDate] = useState(null);

  const activeUserTooltip = "Top 10 Active users as determined by their login count";
  const TopDepAndComTooltip = "Ranking based on Login count";
  const onSort = (category, column, data) => {
    if (!Array.isArray(data) || data.length === 0) return;
    const direction = sort[category]?.column === column && sort[category].direction === "asc" ? "desc" : "asc";

    const sortedData = CommonUtiles.sortData(data, column, direction);

    setStatisticsData((prev) => ({
      ...prev,
      [category === "companies" ? RANKED_COMPANIES_BY_LOGIN_COUNT : RANKED_DEPARTMENTS_BY_LOGIN_COUNT]: sortedData,
    }));
    setSort((prevSort) => ({
      ...prevSort,
      [category]: {
        column,
        direction,
      },
    }));
    const container = document.getElementById(
      category === "companies" ? "company-table-container" : "department-table-container"
    );
    if (container) {
      container.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  // to get statistic list from API
  const getStatisticList = () => {
    setLoading(true);
    ConfigService.getStatisticData()
      .then((response) => {
        setLoading(false);
        setMinStartDate(new Date(response?.data?.startDate));
        setStatisticsData({
          ...response?.data,
          rankedCompaniesByLoginCount: (response?.data?.rankedCompaniesByLoginCount ?? [])?.map((com, index) => ({
            rank: index + 1,
            companyName: com,
          })),
          rankedDepartmentsByLoginCount: (response?.data?.rankedDepartmentsByLoginCount ?? [])?.map((dep, index) => ({
            rank: index + 1,
            departmentName: dep,
          })),
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // use to get updated top 10 active users data
  const getUpdatedTopUsers = () => {
    setUserLoading(true);
    ConfigService.getStatisticData(CommonUtiles.formatDatePayload(startDate), CommonUtiles.formatDatePayload(endDate))
      .then((response) => {
        setUserLoading(false);
        setStatisticsData({
          ...response?.data,
          rankedCompaniesByLoginCount: (response?.data?.rankedCompaniesByLoginCount ?? [])?.map((com, index) => ({
            rank: index + 1,
            companyName: com,
          })), // To Do need to remove as optimization
          rankedDepartmentsByLoginCount: (response?.data?.rankedDepartmentsByLoginCount ?? [])?.map((dep, index) => ({
            rank: index + 1,
            departmentName: dep,
          })), // To Do need to remove as optimization
        });
      })
      .catch(() => {
        setUserLoading(false);
      });
  };

  // use for start date change
  const handleStartChange = (date) => {
    if (!date) {
      setStartDate(null);
      setEndDate(null);
    } else {
      setStartDate(date);
      setEndDate(date);
    }
  };

  // use for end date change
  const handleEndChange = (date) => {
    if (!date) {
      setEndDate(null);
    } else {
      setEndDate(date);
    }
  };

  // use for apply button
  const handleApply = () => {
    if (startDate && endDate) {
      getUpdatedTopUsers();
    }
  };

  useEffect(() => {
    getStatisticList();
  }, []);

  useEffect(() => {
    if (!startDate && !endDate) {
      getUpdatedTopUsers();
    }
  }, [startDate, endDate]);

  // Redirect to Manage user page
  const redirectToUserList = (e, type) => {
    if (!loading) {
      history.push({ pathname: "/users", state: { usertype: type } });
    }
  };

  // keyDown handler for eslint error
  const onKeyDownHandler = (e, type) => {
    if (e.key === "Enter" || e.key === " ") {
      redirectToUserList(e, type);
    }
  };

  return (
    <>
      <div className="d-flex">
        <PageTitle title="Statistics" className="" />
      </div>
      <div className="row gutters-sm">
        <div className="col-xl-9">
          <Row className="gutters-sm">
            <div className="col-xl-6 col-sm-6">
              <div
                className="card py-3 mb-4 px-5 flex-row d-flex curser-pointer align-items-center"
                role="button"
                tabIndex={0}
                onClick={(e) => redirectToUserList(e, "enabled")}
                onKeyDown={(e) => onKeyDownHandler(e, "enabled")}
              >
                <h4 className="text-muted mb-0 flex-grow-1">Enabled Users</h4>
                {loading ? (
                  <Spinner size="small" />
                ) : (
                  <div className="display-4 h-8 text-success">
                    {statisticsData && statisticsData.enabledUsers && statisticsData?.enabledUsers}
                  </div>
                )}
              </div>
            </div>
            <div className="col-xl-6 col-sm-6">
              <div
                role="button"
                className="card py-3 mb-4 px-5 flex-row d-flex curser-pointer align-items-center"
                tabIndex={0}
                onClick={(e) => redirectToUserList(e, "disabled")}
                onKeyDown={(e) => onKeyDownHandler(e, "disabled")}
              >
                <h4 className="text-muted mb-0 flex-grow-1">Disabled Users</h4>
                {loading ? (
                  <Spinner size="small" />
                ) : (
                  <div className="display-4 h-8 text-warning">
                    {statisticsData && statisticsData.disabledUsers && statisticsData?.disabledUsers}
                  </div>
                )}
              </div>
            </div>
          </Row>
          <Row className="gutters-sm">
            <div className="col-xl-12">
              <div className="card mb-xl-0 mb-4">
                <div className="card-header bg-gray-dark">
                  <h4 className="mb-0" data-tip={activeUserTooltip}>
                    Top 10 Active Users
                  </h4>
                  <div className="header-sidebar-form">
                    <div className="d-flex align-items-center">
                      <div className="form-label mb-0 me-2 text-end">Start Date:</div>
                      <DatePicker
                        selected={startDate}
                        onChange={handleStartChange}
                        minDate={minStartDate}
                        maxDate={new Date()}
                        dropdownMode="select"
                        dateFormat="MM/dd/yyyy"
                        className="form-control user-input mb-0"
                        onKeyDown={(e) => e.preventDefault()}
                        isClearable
                        placeholderText="Start Date"
                        disabledKeyboardNavigation
                        onMonthChange={() => document.activeElement.blur()}
                      />
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="form-label mb-0 ms-5 me-2 text-end">End Date:</div>
                      <DatePicker
                        selected={endDate}
                        onChange={handleEndChange}
                        minDate={startDate}
                        maxDate={CommonUtiles.getMaxEndDate(startDate)}
                        disabled={!startDate}
                        dropdownMode="select"
                        dateFormat="MM/dd/yyyy"
                        className="form-control user-input"
                        onKeyDown={(e) => e.preventDefault()}
                        isClearable
                        placeholderText="End Date"
                        disabledKeyboardNavigation
                        onMonthChange={() => document.activeElement.blur()}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-lg btn-primary ms-4"
                      disabled={!startDate || !endDate || userloading}
                      onClick={handleApply}
                    >
                      Apply
                    </button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-outline table-vcenter card-table">
                    <thead className="text-nowrap table-head">
                      <tr>
                        <th className="header-sticky cell-w-80 w-8-res">
                          <span data-tip="Rank">Rank</span>
                        </th>
                        <th className="header-sticky cell-w-150">
                          <span data-tip="Email Address">Email Address</span>
                        </th>
                        <th className="header-sticky cell-w-120">
                          <span data-tip="First Name">First Name</span>
                        </th>
                        <th className="header-sticky cell-w-120">
                          <span data-tip="Last Name">Last Name</span>
                        </th>
                        <th className="header-sticky cell-w-120">
                          <span data-tip="Company">Company</span>
                        </th>
                        <th className="header-sticky cell-w-80">
                          <span data-tip="Role">Role</span>
                        </th>
                        <th className="header-sticky cell-w-130 text-center">
                          <span data-tip="Login Count">Login Count</span>
                        </th>
                        <th className="header-sticky cell-w-170">
                          <span data-tip="Last Login date">Last Login date</span>
                        </th>
                      </tr>
                    </thead>
                    {loading || userloading ? (
                      <tbody>
                        <tr>
                          <td colSpan="8" aria-label="td" className="p-7">
                            <Spinner />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody>
                        {statisticsData &&
                        Object.keys(statisticsData)?.length &&
                        statisticsData?.topUserBasedOnLoginCount?.length > 0 ? (
                          statisticsData?.topUserBasedOnLoginCount?.map((user, index) => {
                            const convertDate = user?.lastLoginTime && CommonUtiles.formatDate(user?.lastLoginTime);
                            return (
                              <tr>
                                <td>
                                  <div className="cell-mx-width">{index + 1}</div>
                                </td>
                                <td>
                                  <div className="cell-mx-width" data-tip={user?.email}>
                                    {user?.email}
                                  </div>
                                </td>
                                <td>
                                  <div className="cell-mx-width" data-tip={user?.name}>
                                    {user?.name}
                                  </div>
                                  <ReactTooltip place="bottom" type="light" effect="solid" />
                                </td>
                                <td>
                                  <div className="cell-mx-width" data-tip={user?.lastName}>
                                    {user?.lastName}
                                  </div>
                                </td>
                                <td>
                                  <div className="cell-mx-width" data-tip={user?.company}>
                                    {user?.company}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className="cell-mx-width capitilize-column"
                                    data-tip={user?.role?.toLowerCase()?.replace(/^\w/, (c) => c.toUpperCase())}
                                  >
                                    {user?.role}
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="cell-mx-width">{user?.loginCount}</div>
                                </td>
                                <td className="text-center">
                                  <div className="cell-mx-width" data-tip={convertDate}>
                                    {convertDate}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="8">
                              <div className="text-center py-5">
                                <div className="mb-5">
                                  <i className="fa fa-2x fa-user text-primary" />
                                </div>
                                <h4 className="text-muted mb-3">{NOT_DATA_FOUND}</h4>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </Row>
        </div>
        <div className="col-xl-3">
          <Row>
            <div className="col-xl-12 col-sm-6">
              <div className="card mb-0 mb-xl-4">
                <div className="card-header bg-gray-dark">
                  <h4 className="mb-0" data-tip={TopDepAndComTooltip}>
                    Top Departments
                  </h4>
                </div>
                <div className="table-responsive h-306" id="department-table-container">
                  <ReactTooltip place="bottom" type="light" effect="solid" />
                  <table className="table table-outline table-vcenter card-table">
                    <thead className="text-nowrap table-head">
                      <tr>
                        <th
                          className="curser-pointer header-sticky cell-w-100"
                          scope="col"
                          onClick={() => onSort("departments", "rank", statisticsData?.rankedDepartmentsByLoginCount)}
                        >
                          <span data-tip="Rank">Rank</span>
                          <span className={CommonUtiles.setArrow(sort.departments, "rank")} />
                        </th>
                        <th
                          onClick={() =>
                            // eslint-disable-next-line prettier/prettier
                        onSort("departments", "departmentName", statisticsData?.rankedDepartmentsByLoginCount)}
                          className="curser-pointer header-sticky"
                          scope="col"
                        >
                          <span data-tip="Departments">Departments</span>
                          <span className={CommonUtiles.setArrow(sort.departments, "departmentName")} />
                        </th>
                      </tr>
                    </thead>
                    {loading ? (
                      <tbody>
                        <tr>
                          <td className="h-265" colSpan="2" aria-label="td">
                            <Spinner />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody>
                        {statisticsData?.rankedDepartmentsByLoginCount &&
                        statisticsData?.rankedDepartmentsByLoginCount?.length ? (
                          statisticsData?.rankedDepartmentsByLoginCount?.map((dep) => (
                            <tr>
                              <td>
                                <div className="cell-mx-width">{dep.rank}</div>
                              </td>
                              <td>
                                <div className="cell-mx-width" data-tip={dep.departmentName}>
                                  {dep.departmentName}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2} className="text-center h-265">
                              <div className="text-center py-5">
                                <div className="mb-5">
                                  <i className="fa fa-2x fa-user text-primary" />
                                </div>
                                <h3 className="text-muted mb-3">No Data Found</h3>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
            <div className="col-xl-12 col-sm-6">
              <div className="card">
                <div className="card-header bg-gray-dark">
                  <h4 className="mb-0" data-tip={TopDepAndComTooltip}>
                    Top Companies
                  </h4>
                </div>
                <div className="table-responsive h-306" id="company-table-container">
                  <ReactTooltip place="bottom" type="light" effect="solid" />
                  <table className="table table-outline table-vcenter card-table">
                    <thead className="text-nowrap table-head">
                      <tr>
                        <th
                          className="curser-pointer header-sticky cell-w-100"
                          onClick={() => onSort("companies", "rank", statisticsData?.rankedCompaniesByLoginCount)}
                        >
                          <span data-tip="Rank">Rank</span>
                          <span className={CommonUtiles.setArrow(sort.companies, "rank")} />
                        </th>
                        <th
                          onClick={() =>
                            // eslint-disable-next-line prettier/prettier
                            onSort("companies", "companyName", statisticsData?.rankedCompaniesByLoginCount)}
                          className="curser-pointer header-sticky"
                          scope="col"
                        >
                          <span data-tip="Companies"> Companies</span>
                          <span className={CommonUtiles.setArrow(sort.companies, "companyName")} />
                        </th>
                      </tr>
                    </thead>
                    {loading ? (
                      <tbody>
                        <tr>
                          <td className="h-265" colSpan="2" aria-label="td">
                            <Spinner />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody>
                        {statisticsData?.rankedCompaniesByLoginCount &&
                        statisticsData?.rankedCompaniesByLoginCount?.length ? (
                          statisticsData?.rankedCompaniesByLoginCount?.map((com) => (
                            <tr>
                              <td>
                                <div className="cell-mx-width">{com.rank}</div>
                              </td>
                              <td>
                                <div className="cell-mx-width" data-tip={com.companyName}>
                                  {com.companyName}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2} className="text-center h-265">
                              <div className="text-center py-5">
                                <div className="mb-5">
                                  <i className="fa fa-2x fa-user text-primary" />
                                </div>
                                <h3 className="text-muted mb-3">No Data Found</h3>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </Row>
        </div>
      </div>
    </>
  );
};
export default StatisticsData;
